import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ScrollRevealText } from "@/components/ScrollRevealText";
import { lazySection } from "@/lib/lazy-section";
import { PromiseScroll } from "@/components/home/PromiseScroll";

const HistorieSection = lazySection(async () => {
  const m = await import("@/components/about/HistorieSection");
  return { default: m.HistorieSection };
}, { minHeight: 600 });
const ParallaxFloatingImages = lazySection(async () => {
  const m = await import("@/components/about/ParallaxFloatingImages");
  return { default: m.ParallaxFloatingImages };
}, { minHeight: 600 });
const AboutFaqSection = lazySection(async () => {
  const m = await import("@/components/about/AboutFaqSection");
  return { default: m.AboutFaqSection };
}, { minHeight: 400 });
const KommInsTeamSection = lazySection(async () => {
  const m = await import("@/components/about/KommInsTeamSection");
  return { default: m.KommInsTeamSection };
}, { minHeight: 400 });
import heroTeam1 from "@/assets/about/hero-team-1.webp.asset.json";
import heroTeam2 from "@/assets/about/hero-team-2.webp.asset.json";
import simonMaiImg from "@/assets/about/simon-mai.webp.asset.json";
import chwaliBoumanImg from "@/assets/about/chwali-bouman.webp.asset.json";
import michaelBuesserImg from "@/assets/about/michael-buesser.webp.asset.json";

export const Route = createFileRoute("/ueber-uns")({
  head: () => ({
    meta: [
      { title: "Über creategy | Agentur für politische Kommunikation" },
      {
        name: "description",
        content:
          "Die Menschen hinter creategy: Spezialagentur für politische Kommunikation mit über zweihundert begleiteten Wahlkämpfen und drei Standorten.",
      },
      { property: "og:title", content: "Über creategy | Agentur für politische Kommunikation" },
      {
        property: "og:description",
        content:
          "Die Menschen hinter creategy: Spezialagentur für politische Kommunikation mit über zweihundert begleiteten Wahlkämpfen und drei Standorten.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://creategy.de/ueber-uns" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Über creategy | Agentur für politische Kommunikation" },
      {
        name: "twitter:description",
        content:
          "Die Menschen hinter creategy: Spezialagentur für politische Kommunikation mit über zweihundert begleiteten Wahlkämpfen und drei Standorten.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://creategy.de/ueber-uns" },
      { rel: "preload", as: "image", href: heroTeam1.url, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { q: "Wofür steht der Name creategy?", a: "creategy verbindet die beiden Wörter, die unsere Arbeit ausmachen: Creativity und Strategy. Kreativität ohne Strategie ist Dekoration, Strategie ohne Kreativität ist Langeweile. Politischer Erfolg entsteht erst, wenn beides zusammenkommt: eine klare Strategie, die den Weg vorgibt, und kreative Umsetzung, die Menschen erreicht und bewegt. Genau an dieser Schnittstelle arbeiten wir, in jedem Wahlkampf und in jedem Kommunikationsprojekt." },
            { q: "Für wen arbeitet creategy?", a: "Wir arbeiten für Bürgermeisterkandidaten, Direktkandidaten für Landtag und Bundestag, Parteien und Fraktionen, Verbände, Berufsgewerkschaften, NGOs und Gemeinden. Was alle unsere Kunden gemeinsam haben: Sie kommunizieren in einem politischen Umfeld und wollen dabei professionell aufgestellt sein." },
            { q: "Ist creategy politisch unabhängig?", a: "Ja. Wir arbeiten für alle demokratischen Parteien, parteilose Kandidaten und Organisationen aller politischen Couleur. Wir haben keine Lieblingspartei. Für Parteien und Bewegungen, die den demokratischen Wettbewerb selbst ablehnen, arbeiten wir nicht." },
            { q: "Begleitet creategy auch Direktkandidaten und Parteien?", a: "Ja. Wir entwickeln Kampagnen für Direktkandidaten auf Landes- und Bundesebene sowie für Parteien und Fraktionen auf Kreis-, Landes- und Bundesebene. Die Mechanik ist dieselbe wie bei Bürgermeisterwahlen: Personenmarke, klare Botschaft, zielgenaue Kommunikation." },
            { q: "Arbeitet creategy auch für Verbände und Gewerkschaften?", a: "Ja. Verbandskommunikation und Personalratswahlen sind ein eigenständiges Geschäftsfeld von creategy. Wir begleiten Verbände, Berufsorganisationen und NGOs bei ihrer laufenden Kommunikation, bei Issue-Kampagnen und bei Personalratswahlen. Zu unseren Kunden gehören unter anderem die Deutsche Polizeigewerkschaft Bayern und der Marburger Bund." },
            { q: "Kann creategy auch Personalratswahlen begleiten?", a: "Ja. Personalratswahlen sind echte Wahlen und brauchen echte Kampagnen. Wir unterstützen Gewerkschaften und Berufsverbände mit demselben Handwerk das wir in politischen Wahlkämpfen einsetzen: Strategie, Kampagnendesign, Mobilisierung und Rhetoriktraining durch Michael Bußer." },
            { q: "Wie unterscheidet sich creategy von anderen Agenturen?", a: "Wir machen ausschließlich politische Kommunikation. Keine Autowerbung, keine Markenkampagnen. Für Wahlkampfkunden bedeutet das: ein Team das schon da war, mit 72,6 Prozent Erfolgsquote und über zweihundert begleiteten Wahlkämpfen im Rücken. Für Verbandskunden bedeutet das: Kampagnendenken aus der Praxis, kombiniert mit echtem Verständnis für Verbandsstrukturen. Dazu kommt in beiden Fällen der Erfahrungsschatz von Senior Advisor Michael Bußer, ehemaliger Regierungssprecher und Staatssekretär a.D." },
            { q: "Arbeitet creategy bundesweit?", a: "Ja. Wir sind mit Standorten in Berlin, Stockach am Bodensee und im Rhein-Main-Gebiet bundesweit aktiv. Für ein Gespür vor Ort kommen wir gerne direkt zu unseren Kunden." },
          ].map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AboutPage,
});

const values = [
  {
    title: "Überparteilich",
    text: "Wir haben keine Lieblingspartei. Aber wir haben eine Lieblingsidee: Demokratie. Wer für sie antritt, kann auf uns zählen. Wer sie in Frage stellt, nicht.",
  },
  {
    title: "Hands on",
    text: "Fester Ansprechpartner, kurze Wege, schnelle Entscheidungen. Wir lösen Probleme, statt sie zu beschreiben. Wenn es brennt, sind wir mit einer Lösung zur Stelle, nicht mit einem Konzeptpapier.",
  },
  {
    title: "Politisch versiert",
    text: "Wir arbeiten täglich an der Schnittstelle von Kommunikation und Politik. Wir kennen ihre Mechanismen, ihre Sprache und ihre Eigenheiten aus über zweihundert Wahlkämpfen und echter Erfahrung.",
  },
  {
    title: "Ergebnisorientiert",
    text: "Wir messen uns am Wahltag, nicht an der Präsentation. Gute Arbeit ist für uns keine Selbstbeschreibung, ist kein schöner Foliensatz, sondern ein erfolgreiches Projekt.",
  },
  {
    title: "Unkonventionell",
    text: "Keine Kampagne von gestern, kein Design, das schon bei drei anderen hing. Wir bringen neue Ideen in ein Feld, das zu lange mit denselben Mitteln gearbeitet hat.",
  },
];

const team = [
  {
    name: "Simon Mai",
    title: "Co-Gründer und Geschäftsführer",
    quote:
      "Mir wird nachgesagt, dass ich alles mache, um eine Wahl zu gewinnen. Und ich muss ehrlich sein: das stimmt.",
    image: simonMaiImg,
  },
  {
    name: "Chwali Bouman",
    title: "Co-Gründer und Geschäftsführer",
    quote:
      "Wir gewinnen Daten – und gewinnen mit Daten. Für faktenbasierte und wirkmächtige Kommunikation.",
    image: chwaliBoumanImg,
  },
  {
    name: "Michael Bußer",
    title: "Senior Advisor",
    quote:
      "Ich habe in Situationen kommuniziert, in denen Fehler keine Option waren. Diese Erfahrung gebe ich heute an unsere Kundinnen und Kunden weiter. Denn der Unterschied zwischen einer guten und einer entscheidenden Botschaft ist kleiner als man denkt und gleichzeitig größer als man hofft.",
    image: michaelBuesserImg,
  },
];

function AboutPage() {
  return (
    <Layout>
      <section aria-labelledby="about-hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1
            id="about-hero"
            className="heading-h1 max-w-5xl"
          >
            Wir sind creategy – Spezialagentur für politische Kommunikation
          </h1>
          <p className="fade-in-up mt-6 max-w-3xl font-body text-xl leading-relaxed text-muted-foreground md:text-2xl">
            Kreativität und Strategie im Dienst der Demokratie.
          </p>

          <div className="mt-12 grid gap-8 md:mt-16 md:grid-cols-2 md:gap-12 lg:gap-16">
            <div className="flex flex-col gap-8">
              <img
                src={heroTeam2.url}
                alt="Zwei creategy-Kolleginnen im Gespräch am Schreibtisch"
                className="w-full h-auto object-contain"
                loading="eager"
                decoding="sync"
                fetchPriority="high"
              />
            </div>
            <div className="flex flex-col gap-8">
              <p className="max-w-md whitespace-pre-line text-base leading-relaxed text-foreground">
                Die Spezialagentur für politischen Erfolg, gegründet aus der
                Überzeugung, dass gute Politik auch gute Kommunikation
                verdient.{"\u00a0"}{"\n\n"}
                Seit 2021 arbeiten wir für Kandidaten, Parteien und
                Organisationen in der ganzen Republik.
              </p>
              <img
                src={heroTeam1.url}
                alt="Drei creategy-Kolleg:innen vor der creategy-Pflanzenwand"
                className="w-full h-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Intro" className="overflow-hidden">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <ScrollRevealText text="Wir haben keine Lieblingspartei. Aber wir haben eine Lieblingsidee: Demokratie. Wer für sie antritt, kann auf uns zählen." />
        </div>
      </section>

      <section aria-labelledby="team" className="bg-background text-foreground">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="team" className="heading-h2 text-primary">
              Die Geschäftsführung.
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-6 font-body text-base leading-relaxed text-muted-foreground md:text-lg">
              Unterstützt wird die Geschäftsführung von einem schlagkräftigen Team an drei Standorten.
            </p>
          </Reveal>

          <ol className="mt-12 flex flex-col gap-8">
            {team.map((m, i) => (
              <li
                key={m.name}
                className="sticky top-32"
                style={{ zIndex: 10 + i }}
              >
                <article className="w-full rounded-[4px] border border-primary bg-primary p-6 shadow-[0_10px_30px_-12px_rgba(0,75,81,0.35)] md:p-8">
                  <div className="grid gap-8 md:grid-cols-[220px_1fr] md:gap-10 md:items-center lg:grid-cols-[340px_1fr] lg:gap-12">
                    {m.image ? (
                      <img
                        src={m.image.url}
                        alt={m.name}
                        className="aspect-square w-full overflow-hidden rounded-[4px] object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div
                        aria-hidden="true"
                        className="aspect-square w-full overflow-hidden rounded-[4px] bg-gradient-to-br from-secondary to-accent"
                      />
                    )}
                    <div>
                      <h3 className="heading-h3 text-primary-foreground">
                        {m.name}
                      </h3>
                      <p className="mt-2 font-body text-base text-brand-button md:text-lg">
                        {m.title}
                      </p>
                      <p className="mt-5 font-body text-base leading-relaxed text-primary-foreground md:text-lg">
                        {m.quote}
                      </p>

                    </div>

                  </div>
                </article>
              </li>
            ))}
          </ol>

        </div>
      </section>


      <HistorieSection />

      <section aria-labelledby="values">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="values" className="heading-h2 max-w-3xl">
              Unsere{" "}
              <span className="font-display font-extrabold">Werte.</span>
            </h2>
          </Reveal>
          <PromiseScroll
            promises={values.map((v) => ({ title: v.title, body: v.text }))}
          />
        </div>
      </section>


      <ParallaxFloatingImages />



      <AboutFaqSection />

      <KommInsTeamSection />
    </Layout>
  );
}
